/**
 * The inbox, the Telegram account wizard and the AI approval queue, as the SPA calls them.
 *
 * Shapes mirror `backend/messaging/serializers.py` and `backend/ai/serializers.py`. Keep
 * them in step: a field the server renames becomes a type error here, not a blank cell.
 */
import { apiRequest, type Paginated } from "@/lib/api";


export type ChannelKind = "telegram" | "instagram";
export type ChannelStatus =
  | "connecting"
  | "active"
  | "disconnected"
  | "disabled"
  | "session_expired"
  | "restricted";
export type ConversationMode = "ai" | "ai_draft" | "human";
export type ConversationStatus = "open" | "waiting_client" | "closed";
export type MessageDirection = "inbound" | "outbound";
export type AuthorKind = "client" | "user" | "ai" | "system";
export type DeliveryStatus = "pending" | "sent" | "delivered" | "read" | "failed";
export type OutboundState = "queued" | "held" | "paused" | "sent" | "failed" | "cancelled";
export type HistoryState = "idle" | "running" | "done" | "failed";
export type DraftState = "pending" | "sent" | "edited" | "rejected" | "expired";
export type ChannelPurpose = "general" | "sales" | "support";
export type AttachmentKind =
  | "image"
  | "voice"
  | "video"
  | "document"
  | "sticker"
  | "location"
  | "contact"
  | "other";

/**
 * Where an Instagram thread stands against the 24-hour reply window. Non-Instagram threads
 * answer `not_instagram`. Only the thread detail carries it.
 */
export type ConversationWindowState = "open" | "closing" | "closed" | "human_tag" | "not_instagram";

export interface ConversationWindow {
  state: ConversationWindowState;
  expires_at: string | null;
  hours_left: number | null;
  reason: string;
}

export type CloseOutcome = "bought" | "refused" | "went_quiet" | "spam";

export interface CloseResult extends Conversation {
  deal_moved: boolean;
  /** Why the deal stayed where it was, when that needs saying. */
  deal_note: string;
}

export interface Conversation {
  client_source_display: string;
  close_outcome: CloseOutcome | "";
  close_outcome_display: string;
  close_reason: number | null;
  close_reason_name: string;
  id: number;
  /** The channel's own thread id: a Telegram chat id as text. Read-only here. */
  external_id: string;
  window: ConversationWindow;
  channel_account: number;
  channel_kind: ChannelKind;
  account_label: string;
  client: number | null;
  client_name: string;
  deal: number | null;
  kind: string;
  title: string;
  assigned_user: number | null;
  assigned_name: string;
  mode: ConversationMode;
  mode_display: string;
  status: ConversationStatus;
  status_display: string;
  in_free_queue: boolean;
  is_overdue: boolean;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
  /** The SLA deadline, server time. Null when the thread is answered. */
  sla_due_at: string | null;
  /** 0 before the first escalation, then 1 (15 min), 2 (45 min), 3 (120 min, the escalation). */
  sla_escalation_level: number;
  unread_count: number;
  /** Why the AI stepped back, when it did. Empty otherwise. */
  handoff_reason: string;
  /** Always `conversation`: an Instagram comment row in the same list says `instagram_comment`. */
  row_kind: "conversation";
  /** An AI draft is waiting for a person in this thread. */
  has_pending_draft: boolean;
  /** The first line of the newest message that reached or came from the client. */
  last_message_text: string;
  last_message_at: string | null;
  /** The plain status word the row shows (09 §7), and the key behind it. */
  status_key: InboxStatusKey;
  status_label: string;
}

export type InboxStatusKey =
  | "closed"
  | "pending_draft"
  | "ai_failed"
  | "unclaimed"
  | "awaiting"
  | "waiting_client";

export type InboxOwnership = "mine" | "unclaimed" | "all";
export type InboxStateFilter = "awaiting" | "pending_draft" | "overdue" | "closed";

/** A number on every chip of the inbox (09 §7). */
export interface InboxCounts {
  ownership: Partial<Record<InboxOwnership, number>>;
  state: Partial<Record<InboxStateFilter, number>>;
  may_see_all: boolean;
  /** The accounts behind the caller's threads: the account and branch filter options. */
  accounts: InboxAccountOption[];
}

export interface InboxAccountOption {
  id: number;
  title: string;
  branch: number | null;
  branch_name: string | null;
}

/** One recorded fact of the deal's brief and who said it (`ai_extracted`, `operator`, …). */
export interface NeedFact {
  value: string | number;
  source: string;
}

export type NeedSummary = Partial<
  Record<"destination" | "date_from" | "date_to" | "adults" | "children" | "service", NeedFact>
>;

export interface StageEntry {
  stage_id: number;
  system_kind: string;
  ready: boolean;
}

/** One variant a message quoted, as frozen on the offer. Brutto: the client's price. */
export interface OfferCard {
  id: number;
  hotel_name: string;
  stars: number | null;
  image_url: string;
  destination: string;
  date_from: string | null;
  nights: number | null;
  meal_label: string;
  pax: number;
  price: string;
  currency: string;
  page_path: string;
}

/** A row of `Murojaatlar`: a thread in the free queue, with what the client first asked. */
export interface QueueRow extends Conversation {
  first_message_text: string;
  client_source: string;
  branch_name: string;
  need: NeedSummary | null;
}

/** The thread's right panel (09 §7, 4.13), built by the server in one read. */
export interface ThreadPanel {
  conversation: number;
  client: { id: number; name: string; phone: string } | null;
  origin: {
    channel_kind: ChannelKind;
    account_label: string;
    client_source: string;
    client_source_display: string;
  };
  first_contact_at: string | null;
  is_returning: boolean;
  deal: number | null;
  need: NeedSummary | null;
  previous_trips: { deal: number; destination: string; date_from: string | null; date_to: string | null }[];
  tasks: { id: number; title: string; due_at: string | null }[];
}

/** §3.5's checks and the agency's own paper list for the thread's deal (decision 9.168). */
export interface DocumentChecklist {
  destination: string;
  citizenships: string[];
  items: string[];
  missing: string;
  checks: { name: string; outcome: string; message: string; travelers: string[] }[];
  passed: number;
  total: number;
  text: string;
}

export interface Attachment {
  id: number;
  kind: AttachmentKind;
  kind_display: string;
  file: string | null;
  /** Where the file stands: `stored`, or why there is no link to show (D630, D634). */
  file_status: "stored" | "pending" | "failed" | "too_large" | "removed" | "none";
  file_retryable: boolean;
  transcript: string;
  ocr_text: string;
  meta: Record<string, unknown>;
}

export interface Message {
  id: number;
  conversation: number;
  direction: MessageDirection;
  author_kind: AuthorKind;
  author_kind_display: string;
  author_user: number | null;
  author_name: string;
  text: string;
  reply_to: number | null;
  sent_at: string;
  delivery_status: DeliveryStatus;
  needs_review: boolean;
  /** An internal note (`Ichki izoh`): never reaches the client. */
  is_note: boolean;
  attachments: Attachment[];
  /** The variants this message quoted (K3's rich card). */
  offer_cards: OfferCard[];
}

/** A phrase from the agency's library. `preview` is null while the thread lacks a value. */
export interface RenderedQuickReply {
  id: number;
  title: string;
  text: string;
  preview: string | null;
  missing: string[];
}

/** One variant the thread's client may be sent. `price` is the decimal string the API gives. */
export interface SendableOffer {
  id: number;
  proposal: number;
  position: number;
  hotel_name: string;
  nights: number | null;
  room: string;
  meal_label: string;
  price: string;
  currency: string;
  expires_at: string;
}

/** One hand-over: the AI stepped back from the thread, with the reason it recorded. */
export interface HandoffEvent {
  run_id: number;
  at: string;
  reason_code: string;
  reason: string;
}

/** Whether any run in the thread found an injection attempt, and what it saw. */
export interface InjectionFlag {
  flagged: boolean;
  runs: number;
  last_at: string | null;
  markers: string[];
}

export interface ThreadEvents {
  injection: InjectionFlag;
  handoffs: HandoffEvent[];
}

export interface OutboundItem {
  id: number;
  conversation: number;
  text: string;
  state: OutboundState;
  state_display: string;
  scheduled_at: string | null;
  attempts: number;
  hold_reason: string;
  last_error: string;
  created_at: string;
}

export interface ChannelAccount {
  id: number;
  kind: ChannelKind;
  kind_display: string;
  label: string;
  purpose: ChannelPurpose;
  branch: number | null;
  status: ChannelStatus;
  status_display: string;
  is_connected: boolean;
  operators: number[];
  only_new_conversations: boolean;
  /** When the owner paused this account (D637). It keeps receiving; it sends nothing. */
  paused_at: string | null;
  last_sync_at: string | null;
  last_error: string;
}

export interface TelegramAccount extends ChannelAccount {
  has_own_app: boolean;
  /** Telegram chat ids the owner has left out of the inbox (D176). */
  excluded_chat_ids: number[];
  last_message_at: string | null;
  flood_wait_until: string | null;
  history_state: HistoryState;
  history_percent: number;
  history_imported: number;
  history_error: string;
}

export type TelegramLoginNext = "code" | "password" | "done";

export interface TelegramLoginStepResponse {
  login_id: string;
  next: TelegramLoginNext;
  expires_at?: string;
  account?: TelegramAccount;
}

export interface AiDraft {
  id: number;
  conversation: number;
  run: number;
  message_kind: string;
  message_kind_display: string;
  text: string;
  sent_text: string;
  state: DraftState;
  state_display: string;
  reject_reason: string;
  decided_by: number | null;
  /** Who sent or rejected it; empty while it waits. */
  decided_by_name: string;
  decided_at: string | null;
  created_at: string;
  /** The knowledge entries the AI quoted ("Manba: …"). Empty when it quoted none. */
  sources: DraftSource[];
}

export interface DraftSource {
  id: number;
  title: string;
  kind: string;
  kind_display: string;
  destination: string;
}

export type ThreadSearch = { search?: string; page?: number };

/** The list endpoint's query, using the backend's filter names (`messaging/filters.py`). */
export type ConversationQuery = Readonly<Record<string, string | number | undefined>>;

export const messagingApi = {
  conversations: (query: ConversationQuery = {}): Promise<Paginated<Conversation>> =>
    apiRequest<Paginated<Conversation>>("/api/messaging/conversations/", { query }),

  conversation: (id: number): Promise<Conversation> =>
    apiRequest<Conversation>(`/api/messaging/conversations/${id}/`),

  /** The chip numbers. Takes the same channel and search as the list. */
  counts: (query: ConversationQuery = {}): Promise<InboxCounts> =>
    apiRequest<InboxCounts>("/api/messaging/conversations/counts/", { query }),

  /** `Murojaatlar`: the free queue, longest wait first. */
  queue: (query: ConversationQuery = {}): Promise<Paginated<QueueRow>> =>
    apiRequest<Paginated<QueueRow>>("/api/messaging/conversations/queue/", { query }),

  /** `Yopish`. A new message from the client reopens the thread. */
  /** `Yopish`: the ending moves the deal; the answer says whether it went. */
  close: (id: number, outcome: CloseOutcome, reason: number | null): Promise<CloseResult> =>
    apiRequest<CloseResult>(`/api/messaging/conversations/${id}/close/`, {
      method: "POST",
      body: { outcome, reason },
    }),

  /** The close undone: the thread is open and the deal back in its column. */
  reopen: (id: number): Promise<CloseResult> =>
    apiRequest<CloseResult>(`/api/messaging/conversations/${id}/reopen/`, { method: "POST" }),

  /** Whether a deal may enter each system stage; "Tur sotib oldi" asks about `Bron`. */
  entryChecklist: (dealId: number): Promise<StageEntry[]> =>
    apiRequest<StageEntry[]>(`/api/deals/${dealId}/entry-checklist/`),

  /** `Bitim yaratish`: 201 with a new deal, 200 with the open one already on the board. */
  createDeal: (id: number): Promise<{ deal: number; created: boolean }> =>
    apiRequest<{ deal: number; created: boolean }>(
      `/api/messaging/conversations/${id}/create-deal/`,
      { method: "POST" },
    ),

  panel: (id: number): Promise<ThreadPanel> =>
    apiRequest<ThreadPanel>(`/api/messaging/conversations/${id}/panel/`),

  documentChecklist: (id: number): Promise<DocumentChecklist> =>
    apiRequest<DocumentChecklist>(`/api/ai/conversations/${id}/document-checklist/`),

  thread: (id: number, query: ThreadSearch = {}): Promise<Paginated<Message>> =>
    apiRequest<Paginated<Message>>(`/api/messaging/conversations/${id}/messages/`, { query }),

  claim: (id: number): Promise<Conversation> =>
    apiRequest<Conversation>(`/api/messaging/conversations/${id}/claim/`, { method: "POST" }),

  release: (id: number): Promise<Conversation> =>
    apiRequest<Conversation>(`/api/messaging/conversations/${id}/release/`, { method: "POST" }),

  assign: (id: number, userId: number): Promise<Conversation> =>
    apiRequest<Conversation>(`/api/messaging/conversations/${id}/assign/`, {
      method: "POST",
      body: { user: userId },
    }),

  /** 202: the reply is queued, and the channel decides when it actually goes. */
  send: (
    id: number,
    text: string,
    idempotencyKey: string,
    quickReplies: readonly string[] = [],
  ): Promise<OutboundItem> =>
    apiRequest<OutboundItem>(`/api/messaging/conversations/${id}/send/`, {
      method: "POST",
      body: {
        text,
        idempotency_key: idempotencyKey,
        // Sent only when there are chips: an absent key means none.
        ...(quickReplies.length === 0 ? {} : { quick_replies: [...quickReplies] }),
      },
    }),

  note: (id: number, text: string): Promise<Message> =>
    apiRequest<Message>(`/api/messaging/conversations/${id}/note/`, {
      method: "POST",
      body: { text },
    }),

  outbound: (id: number): Promise<OutboundItem[]> =>
    apiRequest<OutboundItem[]>(`/api/messaging/conversations/${id}/outbound/`),

  /** "Qayta yuborish": every failed reply of the thread goes back in the queue. */
  retryOutbound: (id: number): Promise<OutboundItem[]> =>
    apiRequest<OutboundItem[]>(`/api/messaging/conversations/${id}/outbound/retry/`, { method: "POST" }),

  /** The AI's hand-overs and the injection flag, as the thread draws them (§4.5). */
  threadEvents: (id: number): Promise<ThreadEvents> =>
    apiRequest<ThreadEvents>(`/api/ai/conversations/${id}/events/`),

  /** One file to the client, with the text as its caption. Queued like a reply (§4.7). */
  sendAttachment: (
    id: number,
    file: File,
    caption: string,
    idempotencyKey: string,
  ): Promise<OutboundItem> => {
    const form = new FormData();
    form.append("file", file);
    form.append("caption", caption);
    form.append("idempotency_key", idempotencyKey);
    return apiRequest<OutboundItem>(`/api/messaging/conversations/${id}/attachments/`, {
      method: "POST",
      body: form,
    });
  },

  /** Ask for a Telegram file again after the automatic attempts gave up (D630). */
  retryAttachment: (id: number, attachmentId: number): Promise<Attachment> =>
    apiRequest<Attachment>(`/api/messaging/conversations/${id}/attachments/${attachmentId}/retry/`, {
      method: "POST",
      body: {},
    }),

  /** "Yozmoqda": the composer's ping while a person types. 204, and 409 when someone else writes. */
  typing: (id: number): Promise<null> =>
    apiRequest<null>(`/api/messaging/conversations/${id}/typing/`, { method: "POST", body: {} }),

  /** The phrase library, filled with this thread's name, destination and date. */
  quickReplies: (id: number): Promise<RenderedQuickReply[]> =>
    apiRequest<RenderedQuickReply[]>(`/api/messaging/conversations/${id}/quick-replies/`),

  /** The offers this client may be sent from this thread, for the picker. */
  sendableOffers: (id: number): Promise<SendableOffer[]> =>
    apiRequest<SendableOffer[]>(`/api/messaging/conversations/${id}/offers/`),

  /** One click: the variant goes out as a formatted message with its public page link. */
  sendOffer: (id: number, variant: number): Promise<OutboundItem> =>
    apiRequest<OutboundItem>(`/api/messaging/conversations/${id}/send-offer/`, {
      method: "POST",
      body: { variant },
    }),

  /**
   * `AI ↔ Odam`. The server refuses a return to AI while the emergency stop is on. An
   * optional `instruction` rides with a return to AI ("AI davom ettirsin") and reaches the
   * model for that stretch of answering only.
   */
  setMode: (id: number, mode: ConversationMode, instruction?: string): Promise<Conversation> =>
    apiRequest<Conversation>(`/api/ai/conversations/${id}/mode/`, {
      method: "POST",
      body: instruction ? { mode, instruction } : { mode },
    }),

  accounts: (): Promise<ChannelAccount[]> =>
    apiRequest<ChannelAccount[]>("/api/messaging/accounts/"),

  telegramAccounts: (): Promise<TelegramAccount[]> =>
    apiRequest<TelegramAccount[]>("/api/messaging/telegram-accounts/"),

  setAccountPaused: (id: number, paused: boolean): Promise<ChannelAccount> =>
    apiRequest<ChannelAccount>(`/api/messaging/accounts/${String(id)}/${paused ? "pause" : "resume"}/`, {
      method: "POST",
    }),
};

export const telegramWizardApi = {
  start: (body: {
    phone: string;
    label?: string;
    api_id?: number;
    api_hash?: string;
  }): Promise<TelegramLoginStepResponse> =>
    apiRequest<TelegramLoginStepResponse>("/api/messaging/telegram-accounts/connect/start/", {
      method: "POST",
      body,
    }),

  code: (loginId: string, code: string): Promise<TelegramLoginStepResponse> =>
    apiRequest<TelegramLoginStepResponse>("/api/messaging/telegram-accounts/connect/code/", {
      method: "POST",
      body: { login_id: loginId, code },
    }),

  password: (loginId: string, password: string): Promise<TelegramLoginStepResponse> =>
    apiRequest<TelegramLoginStepResponse>(
      "/api/messaging/telegram-accounts/connect/password/",
      { method: "POST", body: { login_id: loginId, password } },
    ),

  /**
   * The details step: purpose, branch and the operators who write from the account. The
   * server checks every operator id against this agency's members.
   */
  updateAccount: (
    accountId: number,
    body: { purpose?: ChannelPurpose; branch?: number | null; operators?: number[] },
  ): Promise<TelegramAccount> =>
    apiRequest<TelegramAccount>(`/api/messaging/telegram-accounts/${accountId}/`, {
      method: "PATCH",
      body,
    }),

  reconnect: (accountId: number, phone: string): Promise<TelegramLoginStepResponse> =>
    apiRequest<TelegramLoginStepResponse>(
      `/api/messaging/telegram-accounts/${accountId}/reconnect/`,
      { method: "POST", body: { phone } },
    ),

  /** Answers with the account as it now stands (status `disconnected`). */
  disconnect: (accountId: number): Promise<TelegramAccount> =>
    apiRequest<TelegramAccount>(`/api/messaging/telegram-accounts/${accountId}/disconnect/`, {
      method: "POST",
    }),
};

/** What an account may still send today: the server's numbers, shown before a bulk send. */
export interface BulkQuota {
  per_minute_limit: number;
  per_day_limit: number;
  first_contact_limit: number;
  sent_today: number;
  queued: number;
  first_contacts_today: number;
  remaining_today: number;
  remaining_first_contacts: number;
}

export interface BulkSendBody {
  account: number;
  text: string;
  conversation_ids: number[];
}

/** `Ommaviy xabar`. The send is refused whole when it would exceed the quota. */
export const bulkApi = {
  quota: (accountId: number): Promise<BulkQuota> =>
    apiRequest<BulkQuota>("/api/messaging/bulk/", { query: { account: accountId } }),

  send: (body: BulkSendBody): Promise<BulkQuota> =>
    apiRequest<BulkQuota>("/api/messaging/bulk/", { method: "POST", body }),
};

export type TaskKindValue =
  | "call"
  | "reply"
  | "follow_up"
  | "collect_payment"
  | "request_document"
  | "request_review"
  | "other";

export interface TaskCreateBody {
  title: string;
  kind: TaskKindValue;
  note?: string;
  client?: number | null;
  /** A deal's reminder names its deal; a thread's task names the conversation instead. */
  deal?: number | null;
  assignee?: number | null;
  due_at?: string | null;
  subject_kind?: string;
  subject_id?: string;
}

/** `Vazifa yaratish` from a thread: the task names the conversation it came from. */
export const taskApi = {
  create: (body: TaskCreateBody): Promise<{ id: number }> =>
    apiRequest<{ id: number }>("/api/tasks/", { method: "POST", body }),
};

export interface ClientIdentity {
  id: number;
  client: number;
  kind: string;
  kind_display: string;
  value: string;
  username: string;
  display_name: string;
  confidence: string;
  confidence_display: string;
  is_primary: boolean;
  verified_at: string | null;
}

export interface ClientMerge {
  id: number;
  primary: number;
  primary_name: string;
  merged: number;
  merged_name: string;
  method: string;
  method_display: string;
  moved_rows: Record<string, number[]> | Record<string, unknown>;
  note: string;
  is_undone: boolean;
  created_at: string;
}

/** A client's recognised identities, and the merges that folded records into it (`Ajratish`). */
export const clientLinksApi = {
  identities: (clientId: number): Promise<ClientIdentity[]> =>
    apiRequest<ClientIdentity[]>(`/api/clients/${clientId}/identities/`),

  merges: (clientId: number): Promise<Paginated<ClientMerge>> =>
    apiRequest<Paginated<ClientMerge>>("/api/client-merges/", { query: { primary: clientId } }),

  /** `Ajratish`: reverses one merge. The server refuses a merge that was already undone. */
  undoMerge: (mergeId: number): Promise<ClientMerge> =>
    apiRequest<ClientMerge>(`/api/client-merges/${mergeId}/undo/`, { method: "POST" }),
};

export const aiDraftsApi = {
  pending: (conversationId?: number): Promise<Paginated<AiDraft>> =>
    apiRequest<Paginated<AiDraft>>("/api/ai/drafts/", {
      query: { state: "pending", conversation: conversationId },
    }),

  /** Every draft of one thread, decided ones included: who approved an AI message (4.8). */
  forConversation: (conversationId: number): Promise<Paginated<AiDraft>> =>
    apiRequest<Paginated<AiDraft>>("/api/ai/drafts/", { query: { conversation: conversationId } }),

  send: (id: number, text?: string): Promise<unknown> =>
    apiRequest<unknown>(`/api/ai/drafts/${id}/send/`, {
      method: "POST",
      body: text === undefined ? {} : { text },
    }),

  reject: (id: number, reason: string): Promise<unknown> =>
    apiRequest<unknown>(`/api/ai/drafts/${id}/reject/`, { method: "POST", body: { reason } }),
};
